import { t } from '@ctcells/i18n'
import type { ReactNode } from 'react'

import { Tabs, type TabItem } from './Tabs.tsx'

import './layout.css'

export interface FigurePanelProps<K extends string = string> {
  /** 그림 탭(FIGTABS). 「전체」가 항상 첫 항목이고 모듈마다 구성이 다르다(§14-3) */
  tabs: readonly TabItem<K>[]
  value: K
  onChange: (id: K) => void
  /** 탭 줄 왼쪽 슬롯. 패드의 「실행 데모」·「수거 완료」처럼 이 패널에 딸린 버튼만 둔다 */
  leading?: ReactNode
  /** 포커스 뷰 — 고른 컴포넌트 그림. 🔴 그림에 제목을 붙이지 않는다(탭이 이름 역할 · §14-3) */
  children: ReactNode
  /**
   * 오른쪽 사이드 패널. 넘기면 2열(그림 + 440px)로 갈라진다.
   * 「전체」는 그림만이라 이걸 넘기지 않는다 — 대신 `legend` 를 쓴다.
   */
  sidePanel?: ReactNode
}

/**
 * 모니터링 · 패드 공통 뷰 — README §14-3.
 *
 * ```
 * ┌ .fighead ───────────────────── [전체][트레이][원심분리기][비전][3축 로봇] ┐
 * ├───────────────────────────┬──────────────────────────────────────────────┤
 * │ 포커스 뷰 (제목 없음)      │ 사이드 패널 440px (제목 없음 · 제어 없음)     │
 * └───────────────────────────┴──────────────────────────────────────────────┘
 * ```
 *
 * 🔴 **탭 위치는 패널 오른쪽 위 고정이다. 절대 옮기지 마라** — eric 이 여러 번 지적한 지점이다.
 *    `.fighead` 가 `justify-content:flex-end`(838행)라 `leading` 이 있든 없든,
 *    사이드 패널이 열리든 닫히든 탭의 오른쪽 끝 위치가 움직이지 않는다.
 *
 * 탭을 바꾸면 사이드 패널이 닫힌다 — 그 판단은 화면(호출부)이 한다.
 */
export function FigurePanel<K extends string>({
  tabs,
  value,
  onChange,
  leading,
  children,
  sidePanel,
}: FigurePanelProps<K>) {
  return (
    <div className="figpanel">
      <div className="fighead">
        {leading ? <div className="fighead__leading">{leading}</div> : null}
        <Tabs
          items={tabs}
          value={value}
          onChange={onChange}
          label={t('figTab.aria')}
          variant="fig"
        />
      </div>
      <div className={`figbody${sidePanel ? ' figbody--split' : ''}`}>
        <div className="figbody__focus">{children}</div>
        {sidePanel}
      </div>
    </div>
  )
}

export interface FigureLegendItem {
  /** 표식 그림(사각·점선 사각·체크 원). 도형은 화면이 그린다 */
  mark: ReactNode
  /** 표식 뜻. 굵게 나가는 앞 라벨 */
  term?: string
  /** 설명 */
  text: string
}

/**
 * 「전체」 보기 오른쪽 아래 범례 상자 — `figLegend()` 1654행 · `.figleg` 862행.
 *
 * 🔴 **와이어프레임 전체에서 범례가 허용된 유일한 자리다**(§1-2). eric 이 직접 요청한 것이고
 *    "엑셀의 범례 느낌"이라고 했다. 다른 화면에 범례를 복제하지 마라.
 */
export function FigureLegend({ items }: { items: readonly FigureLegendItem[] }) {
  return (
    <div className="figleg" role="group" aria-label={t('panel.legend')}>
      {items.map((item) => (
        <span key={item.text} className="figleg__row">
          <span className="figleg__mark">{item.mark}</span>
          {item.term ? <b>{item.term}</b> : null}
          {item.text}
        </span>
      ))}
    </div>
  )
}
